import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { NavigateArgs, StaticSettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { Button, DropdownField, Truncated, FormConfig, FieldProps, BaseFieldProps } from '@frontend/design-system';

export type KnowledgeSourceOption<T extends string> = {
  value: T;
  label: string;
  iconName?: IconName;
  settingsPageLink?: NavigateArgs<StaticSettingsRoutes>;
};

export type DropdownKeys<TFields extends FormConfig> = Extract<
  {
    [K in keyof TFields]: TFields[K] extends { type: 'dropdown' } ? K : never;
  }[keyof TFields],
  string
>;

type Props<TFields extends FormConfig, G extends DropdownKeys<TFields>, T extends string> = {
  fieldProps: FieldProps<TFields, G> & BaseFieldProps<string>;
  options: KnowledgeSourceOption<T>[];
  renderSourceData: (source: T) => ReactNode;
};

export const KnowledgeSourceInput = <TFields extends FormConfig, G extends DropdownKeys<TFields>, T extends string>({
  fieldProps,
  options,
  renderSourceData,
}: Props<TFields, G, T>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const source = fieldProps?.value as T;
  const selectedOption = options.find((opt) => opt.value === source);

  return (
    <div css={styles.container}>
      <div css={styles.dropdownContainer}>
        <DropdownField {...fieldProps} label='' placeholder={t('Select One')} css={styles.dropdownField}>
          {options.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              <span css={styles.optionContent}>
                <Icon name={option.iconName || 'view-source'} size={16} />
                <Truncated>{option.label}</Truncated>
              </span>
            </DropdownField.Option>
          ))}
        </DropdownField>
        {selectedOption?.settingsPageLink && (
          <Button
            variant='secondary'
            css={styles.arrowButton}
            onClick={() => {
              !!selectedOption.settingsPageLink && settingsNavigate(selectedOption.settingsPageLink);
            }}
            hoverLabel={t('Go to Settings')}
          >
            <Icon name='arrow-right' size={24} />
          </Button>
        )}
      </div>

      {renderSourceData(source)}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    padding: var(--fabric-space-padding-xxs);
    background-color: var(--fabric-color-neutral-5);
    border-radius: var(--fabric-radius-sm);
  `,
  dropdownContainer: css`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--fabric-space-gap-xs);
    overflow: hidden;
    > div:first-child {
      overflow: hidden;
    }
  `,
  dropdownField: css`
    background-color: var(--fabric-color-surface-default);
    overflow: hidden;
  `,
  optionContent: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
  `,
  arrowButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--fabric-space-padding-xs);
    background-color: var(--fabric-color-surface-default);
    cursor: pointer;
    height: 40px;
    width: 40px;
    flex-shrink: 0;
  `,
};
